import { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { EmptyState, SectionCard, Spinner } from "@/components/shared";

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const btnPrimary = "inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1 block">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function ActiveBadge({ active }) {
  return active
    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">Active</span>
    : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>;
}

/* ──────────────────────────────────────────── Admin Dashboard */
export function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  if (loading) return <Spinner />;
  if (!data) return <EmptyState icon="ServerCrash" title="Dashboard unavailable" />;
  const w = data.widgets || {};
  const cards = [
    { key: "active_users", label: "Active Users", value: w.active_users, icon: "UserCheck", tone: "emerald" },
    { key: "pending_users", label: "Pending Users", value: w.pending_users, icon: "UserCog", tone: "amber" },
    { key: "disabled_users", label: "Disabled Users", value: w.disabled_users, icon: "UserX", tone: "slate" },
    { key: "active_teams", label: "Active Teams", value: w.active_teams, icon: "Users", tone: "blue" },
    { key: "active_services", label: "Active Services", value: w.active_services, icon: "Package", tone: "blue" },
    { key: "active_templates", label: "Comm Templates", value: w.active_templates, icon: "FileText", tone: "blue" },
    { key: "audit_log_activity", label: "Audit Events", value: w.audit_log_activity, icon: "ScrollText", tone: "slate" },
  ];
  const tones = {
    emerald: "bg-emerald-50 text-emerald-600", amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-100 text-slate-500", blue: "bg-blue-50 text-blue-600", red: "bg-red-50 text-red-600",
  };
  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Ico = Icons[c.icon] || Icons.Square;
          return (
            <div key={c.key} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`admin-widget-${c.key}`}>
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center mb-3 ${tones[c.tone]}`}><Ico className="h-4 w-4" /></div>
              <div className="text-2xl font-semibold text-slate-900">{c.value ?? 0}</div>
              <div className="text-xs text-slate-500 mt-0.5">{c.label}</div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Recent Configuration Changes" icon="Settings2">
          <RecentList rows={data.recent_config_changes} testid="recent-config" />
        </SectionCard>
        <SectionCard title="Recent Permission Changes" icon="ShieldCheck">
          <RecentList rows={data.recent_permission_changes} testid="recent-perms" />
        </SectionCard>
      </div>
    </div>
  );
}

function RecentList({ rows, testid }) {
  if (!rows || rows.length === 0) return <p className="text-sm text-slate-500 py-2">No recent activity.</p>;
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={`${r.action}-${i}`} className="flex items-start gap-3 text-sm border-b border-slate-50 pb-2 last:border-0">
          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono shrink-0">{r.action}</span>
          <div className="min-w-0">
            <p className="text-slate-700 truncate">{r.summary}</p>
            <p className="text-xs text-slate-400">{r.user_name} · {r.created_at ? new Date(r.created_at).toLocaleString() : ""}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────── Services */
function ServiceDialog({ service, teams, onSaved, canManage }) {
  const editing = !!service;
  const [open, setOpen] = useState(false);
  const blank = { name: "", category: "consultation", description: "", scope: "", base_price: 0, currency: "USD", assigned_team: "", active_flag: true };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(editing ? { ...blank, ...service } : blank); }, [open]); // eslint-disable-line
  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, base_price: Number(form.base_price) || 0, assigned_team: form.assigned_team || null };
      if (editing) await api.patch(`/admin/services/${service.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Service updated" : "Service created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  if (!canManage && !editing) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`service-edit-${service.id}`}><Icons.Pencil className="h-4 w-4" /></button>
          : <button className={btnPrimary} data-testid="add-service-button"><Icons.Plus className="h-4 w-4" /> Add Service</button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="service-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Service" : "Add Service"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><input className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="service-category" /></Field>
            <Field label="Assigned Team">
              <Select value={form.assigned_team || "none"} onValueChange={(v) => setForm({ ...form, assigned_team: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="service-team"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {(teams || []).map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Description"><textarea className={inputCls} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="service-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Base Price"><input type="number" className={inputCls} value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="service-price" /></Field>
            <Field label="Currency"><input className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="service-currency" /></Field>
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Active</span>
            <Switch checked={form.active_flag} onCheckedChange={(v) => setForm({ ...form, active_flag: v })} data-testid="service-active" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={saving || !form.name} className={btnPrimary} data-testid="service-save">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ServicesTab({ teams, canManage }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/services").then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const money = (n, c) => `${c || "USD"} ${Number(n || 0).toLocaleString()}`;
  return (
    <div data-testid="services-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Service catalog. Pricing here is the default for new estimates; accepted estimates stay immutable.</p>
        <ServiceDialog teams={teams} canManage={canManage} onSaved={load} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="services-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="px-5 py-3 font-semibold">Name</th>
            <th className="px-5 py-3 font-semibold hidden sm:table-cell">Category</th>
            <th className="px-5 py-3 font-semibold">Base Price</th>
            <th className="px-5 py-3 font-semibold">Status</th>
            <th className="px-5 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No services.</td></tr> :
              rows.map((s) => (
                <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`service-row-${s.id}`}>
                  <td className="px-5 py-3 text-slate-800 font-medium">{s.name}</td>
                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell capitalize">{(s.category || "").replace(/_/g, " ")}</td>
                  <td className="px-5 py-3 text-slate-700">{money(s.base_price, s.currency)}</td>
                  <td className="px-5 py-3"><ActiveBadge active={s.active_flag} /></td>
                  <td className="px-5 py-3 text-right">{canManage && <ServiceDialog service={s} teams={teams} canManage={canManage} onSaved={load} />}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────── Add-ons */
function AddonDialog({ addon, onSaved, canManage }) {
  const editing = !!addon;
  const [open, setOpen] = useState(false);
  const blank = { name: "", description: "", price: 0, currency: "USD", active_flag: true };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setForm(editing ? { ...blank, ...addon, price: addon.price ?? addon.base_price ?? 0 } : blank); }, [open]); // eslint-disable-line
  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0 };
      if (editing) await api.patch(`/admin/addons/${addon.id}`, payload);
      else await api.post("/admin/addons", payload);
      toast.success(editing ? "Add-on updated" : "Add-on created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  if (!canManage && !editing) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`addon-edit-${addon.id}`}><Icons.Pencil className="h-4 w-4" /></button>
          : <button className={btnPrimary} data-testid="add-addon-button"><Icons.Plus className="h-4 w-4" /> Add Add-on</button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="addon-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Add-on" : "Add Add-on"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="addon-name" /></Field>
          <Field label="Description"><textarea className={inputCls} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="addon-description" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Price"><input type="number" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="addon-price" /></Field>
            <Field label="Currency"><input className={inputCls} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} data-testid="addon-currency" /></Field>
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Active</span>
            <Switch checked={form.active_flag} onCheckedChange={(v) => setForm({ ...form, active_flag: v })} data-testid="addon-active" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={saving || !form.name} className={btnPrimary} data-testid="addon-save">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddonsTab({ canManage }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/addons").then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const money = (n, c) => `${c || "USD"} ${Number(n || 0).toLocaleString()}`;
  return (
    <div data-testid="addons-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">Add-on catalog attachable to services and estimates.</p>
        <AddonDialog canManage={canManage} onSaved={load} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="addons-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="px-5 py-3 font-semibold">Name</th>
            <th className="px-5 py-3 font-semibold hidden sm:table-cell">Description</th>
            <th className="px-5 py-3 font-semibold">Price</th>
            <th className="px-5 py-3 font-semibold">Status</th>
            <th className="px-5 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No add-ons.</td></tr> :
              rows.map((a) => (
                <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`addon-row-${a.id}`}>
                  <td className="px-5 py-3 text-slate-800 font-medium">{a.name}</td>
                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell truncate max-w-xs">{a.description || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{money(a.base_price ?? a.price, a.currency)}</td>
                  <td className="px-5 py-3"><ActiveBadge active={a.active_flag} /></td>
                  <td className="px-5 py-3 text-right">{canManage && <AddonDialog addon={a} canManage={canManage} onSaved={load} />}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────── Document Categories */
function CategoryDialog({ category, onSaved, canManage }) {
  const editing = !!category;
  const [open, setOpen] = useState(false);
  const blank = { label: "", description: "", client_visible_default: true, requires_review: false, active: true };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm(editing ? {
      ...blank, ...category,
      label: category.label || "",
      client_visible_default: category.client_visible_default ?? true,
      requires_review: category.requires_review ?? false,
      active: category.active ?? true,
    } : blank);
  }, [open]); // eslint-disable-line
  const submit = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/admin/document-categories/${category.id}`, form);
      else await api.post("/admin/document-categories", form);
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  if (!canManage && !editing) return null;
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`category-edit-${category.id}`}><Icons.Pencil className="h-4 w-4" /></button>
          : <button className={btnPrimary} data-testid="add-category-button"><Icons.Plus className="h-4 w-4" /> Add Category</button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="category-dialog">
        <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><input className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="category-name" /></Field>
          <Field label="Description"><textarea className={inputCls} rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="category-description" /></Field>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Client-visible by default</span>
            <Switch checked={form.client_visible_default} onCheckedChange={(v) => setForm({ ...form, client_visible_default: v })} data-testid="category-visible" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Requires review</span>
            <Switch checked={form.requires_review} onCheckedChange={(v) => setForm({ ...form, requires_review: v })} data-testid="category-review" />
          </div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <span className="text-sm font-medium text-slate-700">Active</span>
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="category-active" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={saving || !form.label} className={btnPrimary} data-testid="category-save">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DocCategoriesTab({ canManage }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/admin/document-categories").then((r) => setRows(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="categories-tab">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-slate-500">One canonical taxonomy — these categories power the classification dropdowns across staff document upload, the client portal and Document Intelligence.</p>
        <CategoryDialog canManage={canManage} onSaved={load} />
      </div>
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="categories-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
            <th className="px-5 py-3 font-semibold">Name</th>
            <th className="px-5 py-3 font-semibold hidden md:table-cell">Description</th>
            <th className="px-5 py-3 font-semibold text-center">Client-visible</th>
            <th className="px-5 py-3 font-semibold text-center">Review</th>
            <th className="px-5 py-3 font-semibold">Status</th>
            <th className="px-5 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} className="px-5 py-8 text-center text-slate-500">No categories.</td></tr> :
              rows.map((c) => (
                <tr key={c.id || c.key} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`category-row-${c.id}`}>
                  <td className="px-5 py-3 text-slate-800 font-medium">{c.label}</td>
                  <td className="px-5 py-3 text-slate-500 hidden md:table-cell truncate max-w-xs">{c.description || "—"}</td>
                  <td className="px-5 py-3 text-center">{(c.client_visible_default ?? true) ? <Icons.Check className="h-4 w-4 text-emerald-600 inline" /> : <Icons.Minus className="h-4 w-4 text-slate-300 inline" />}</td>
                  <td className="px-5 py-3 text-center">{c.requires_review ? <Icons.Check className="h-4 w-4 text-amber-600 inline" /> : <Icons.Minus className="h-4 w-4 text-slate-300 inline" />}</td>
                  <td className="px-5 py-3"><ActiveBadge active={c.active} /></td>
                  <td className="px-5 py-3 text-right">{canManage && <CategoryDialog category={c} canManage={canManage} onSaved={load} />}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────── Settings (system / portal / penny) */
function prettyLabel(k) { return k.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase()); }

function SettingsGroup({ group, canManage }) {
  const [values, setValues] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => {
    api.get(`/admin/settings/${group}`).then((r) => setValues(r.data.values || {})).catch(() => setValues({}));
  }, [group]);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/settings/${group}`, { values });
      toast.success(`${prettyLabel(group)} settings saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  if (values === null) return <Spinner />;
  const entries = Object.entries(values);
  const isColor = (k, v) => typeof v === "string" && (k.toLowerCase().includes("color") || /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v));
  return (
    <div className="space-y-3" data-testid={`settings-${group}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5 bg-white">
            <span className="text-sm font-medium text-slate-700">{prettyLabel(k)}</span>
            {typeof v === "boolean" ? (
              <Switch checked={v} disabled={!canManage} onCheckedChange={(nv) => setValues({ ...values, [k]: nv })} data-testid={`setting-${group}-${k}`} />
            ) : isColor(k, v) ? (
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v) ? v : "#171B2E"}
                  disabled={!canManage}
                  onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                  className="h-8 w-9 rounded-md border border-slate-300 cursor-pointer p-0.5 bg-white disabled:cursor-not-allowed"
                  data-testid={`setting-color-${group}-${k}`}
                  aria-label={`${prettyLabel(k)} color picker`}
                />
                <input
                  className="border border-slate-300 rounded-md px-2 py-1 text-sm w-28 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={v ?? ""}
                  disabled={!canManage}
                  onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                  data-testid={`setting-${group}-${k}`}
                />
              </div>
            ) : (
              <input
                className="border border-slate-300 rounded-md px-2 py-1 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
                type={typeof v === "number" ? "number" : "text"}
                value={v ?? ""}
                disabled={!canManage}
                onChange={(e) => setValues({ ...values, [k]: typeof v === "number" ? Number(e.target.value) : e.target.value })}
                data-testid={`setting-${group}-${k}`}
              />
            )}
          </div>
        ))}
      </div>
      {canManage && (
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className={btnPrimary} data-testid={`save-settings-${group}`}>
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Save {prettyLabel(group)} Settings
          </button>
        </div>
      )}
    </div>
  );
}

export function SettingsTab({ canManage }) {
  return (
    <div data-testid="settings-tab">
      <Tabs defaultValue="system">
        <TabsList className="mb-4">
          <TabsTrigger value="system" data-testid="settings-tab-system">System</TabsTrigger>
          <TabsTrigger value="portal" data-testid="settings-tab-portal">Client Portal</TabsTrigger>
          <TabsTrigger value="currency" data-testid="settings-tab-currency">Currency</TabsTrigger>
          <TabsTrigger value="estimate_followup" data-testid="settings-tab-estimate-followup">Estimate Follow-Up</TabsTrigger>
        </TabsList>
        <TabsContent value="system"><SettingsGroup group="system" canManage={canManage} /></TabsContent>
        <TabsContent value="portal"><SettingsGroup group="portal" canManage={canManage} /></TabsContent>
        <TabsContent value="currency">
          <p className="text-sm text-slate-500 mb-3">Presentation only. Sets the USD⇄THB rate used to show equivalent prices across estimates and engagements. Canonical service prices and invoicing are never changed. <span className="font-medium">Usd Thb Rate</span> = Thai baht per 1 USD.</p>
          <SettingsGroup group="currency" canManage={canManage} />
        </TabsContent>
        <TabsContent value="estimate_followup">
          <p className="text-sm text-slate-500 mb-3">
            Commercial follow-up automation thresholds. All values are <span className="font-medium">calendar days</span>. The hourly sweep notifies the assigned staff member when these thresholds are crossed — respecting a repeat-reminder cooldown to prevent duplicates.
          </p>
          <SettingsGroup group="estimate_followup" canManage={canManage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* Rules (placeholder shell) — RETIRED in Administration Cleanup Package A (Vol 5). Non-executing config surface, zero runtime consumers. Rule ownership stays with domain engines (Business Rules Engine, Assignment engine, Workflow framework). */

/* Automations (placeholder shell) — RETIRED in Administration Cleanup Package B (Vol 5). No execution engine, scheduler, worker, retry, or consumer; the "Failed Automations" metric was mathematically always 0. Real automations are owned by domain engines (lead_automation/communications/finance/mailer/etc.); operational recovery is owned by Operations Center. */

/* ──────────────────────────────────────────── Clone Role dialog (used in Roles tab) */
export function CloneRoleDialog({ roles, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", description: "", clone_from: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/admin/roles", { label: form.label, description: form.description, clone_from: form.clone_from || null });
      toast.success(`Role "${form.label}" created`);
      setOpen(false); setForm({ label: "", description: "", clone_from: "" }); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg px-3 py-2 text-sm font-medium" data-testid="clone-role-button">
          <Icons.Copy className="h-4 w-4" /> New / Clone Role
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="clone-role-dialog">
        <DialogHeader><DialogTitle className="font-heading">New Role</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Role name"><input className={inputCls} value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="clone-role-label" /></Field>
          <Field label="Description"><input className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="clone-role-description" /></Field>
          <Field label="Clone permissions from" hint="Optional — start with an existing role's access.">
            <Select value={form.clone_from || "none"} onValueChange={(v) => setForm({ ...form, clone_from: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="clone-role-source"><SelectValue placeholder="Start empty" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Start empty</SelectItem>
                {(roles || []).map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter>
          <button onClick={submit} disabled={saving || !form.label} className={btnPrimary} data-testid="clone-role-save">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Create Role
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
